import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PedidoCompleto {
  id: number;
  orcamento_id: number;
  projeto_id: number;
  cliente_id: number;
  valor_total: number;
  prazo_entrega: number;
  status: string;
  situacao_renovacao?: string;
  tipo_negocio?: string;
  numero_ciclo?: number | null;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  orcamento?: {
    id: number;
    valor_total: number;
    status: string;
    plano_id?: string;
    quantidade_placas?: number;
    valor_seguro?: number;
    vigencia_anos?: number;
    data_aprovacao?: string;
    aprovado_por?: string;
  };
  projeto?: {
    id: number;
    valor_projeto: number;
    status: string;
    departamento: string;
    observacoes?: string;
  };
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cpf_cnpj?: string;
  };
  contrato?: {
    id: number;
    numero_contrato: string;
    valor: number;
    data_inicio: string;
    data_fim: string;
    status: string;
  };
}

export function usePedidoPorEvento(evento: any) {
  return useQuery({
    queryKey: ['pedido-por-evento', evento?.id],
    queryFn: async () => {
      if (!evento) return null;

      console.log('Buscando pedido para evento:', evento);
      console.log('Título do evento:', evento.titulo);
      console.log('Cliente ID:', evento.cliente_id);
      console.log('Contrato ID:', evento.contrato_id);

      let pedidoId: number | null = evento.pedido_id ? Number(evento.pedido_id) : null;

      // Extrair ID do pedido do título do evento
      if (!pedidoId && evento.titulo) {
        const match = evento.titulo.match(/- (\d+) -/);
        if (match) {
          pedidoId = parseInt(match[1]);
        }
      }

      // Se não encontrou no título, tentar pelo contrato
      if (!pedidoId && evento.contrato_id) {
        const { data: contratoData } = await supabase
          .from('contratos')
          .select('pedido_id, pedido:pedidos(id)')
          .eq('id', evento.contrato_id)
          .single();

        const pedidoFromContrato = contratoData?.pedido as { id?: number } | { id?: number }[] | null;
        if (Array.isArray(pedidoFromContrato)) {
          pedidoId = pedidoFromContrato[0]?.id ?? null;
        } else if (pedidoFromContrato?.id) {
          pedidoId = pedidoFromContrato.id;
        }
        if (!pedidoId && (contratoData as { pedido_id?: number })?.pedido_id) {
          pedidoId = (contratoData as { pedido_id: number }).pedido_id;
        }
      }

      // Se ainda não encontrou, tentar buscar por cliente e data
      if (!pedidoId && evento.cliente_id && evento.data_inicio) {
        console.log('Buscando pedido por cliente e data...');
        
        // Buscar pedidos do cliente criados próximos à data do evento
        const dataEvento = new Date(evento.data_inicio);
        const dataInicio = new Date(dataEvento);
        dataInicio.setDate(dataInicio.getDate() - 7); // 7 dias antes
        const dataFim = new Date(dataEvento);
        dataFim.setDate(dataFim.getDate() + 7); // 7 dias depois

        const { data: pedidosData } = await supabase
          .from('pedidos')
          .select('id, created_at')
          .eq('cliente_id', evento.cliente_id)
          .gte('created_at', dataInicio.toISOString())
          .lte('created_at', dataFim.toISOString())
          .order('created_at', { ascending: false })
          .limit(1);

        if (pedidosData && pedidosData.length > 0) {
          pedidoId = pedidosData[0].id;
          console.log('Pedido encontrado por cliente e data:', pedidoId);
        }
      }

      // Se ainda não encontrou, tentar buscar o pedido mais recente do cliente
      if (!pedidoId && evento.cliente_id) {
        console.log('Buscando pedido mais recente do cliente...');
        
        const { data: pedidosData } = await supabase
          .from('pedidos')
          .select('id, created_at')
          .eq('cliente_id', evento.cliente_id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (pedidosData && pedidosData.length > 0) {
          pedidoId = pedidosData[0].id;
          console.log('Pedido mais recente encontrado:', pedidoId);
        }
      }

      if (!pedidoId) {
        console.log('ID do pedido não encontrado para o evento');
        return null;
      }

      console.log('ID do pedido encontrado:', pedidoId);

      // Buscar informações completas do pedido
      console.log('Buscando pedido com ID:', pedidoId);
      
      const { data, error } = await supabase
        .from('pedidos')
        .select(`
          *,
          orcamento:orcamentos!orcamento_id(id, valor_total, status, plano_id, quantidade_placas, valor_seguro, vigencia_anos, data_aprovacao, aprovado_por),
          projeto:projetos(*),
          cliente:clientes(*),
          contrato:contratos(*)
        `)
        .eq('id', pedidoId)
        .single();

      if (error) {
        console.error('Erro ao buscar pedido:', error);
        throw error;
      }

      console.log('Pedido encontrado com sucesso:', data);

      console.log('Pedido encontrado:', data);
      return data as PedidoCompleto;
    },
    enabled: !!evento,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
} 